import React, { useEffect, useMemo, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import useRestaurantContext from "../../hooks/useRestaurantContext";
import {
  ReportCard,
  ReportChart,
  ReportFilterBar,
  ReportTable,
} from "../../components/reports";

const defaultDateRange = { preset: "30d", start: "", end: "" };

// ---------- helpers ----------

const getOrderDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const resolveDateRange = (dateRange) => {
  const now = new Date();
  let start = null;
  let end = null;

  if (dateRange.preset === "today") {
    start = new Date();
    start.setHours(0, 0, 0, 0);
    end = new Date(start);
    end.setDate(end.getDate() + 1);
  } else if (dateRange.preset === "7d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "30d") {
    end = new Date(now);
    end.setHours(23, 59, 59, 999);
    start = new Date(end);
    start.setDate(start.getDate() - 29);
    start.setHours(0, 0, 0, 0);
  } else if (dateRange.preset === "custom") {
    if (dateRange.start) {
      start = new Date(dateRange.start);
      start.setHours(0, 0, 0, 0);
    }
    if (dateRange.end) {
      end = new Date(dateRange.end);
      end.setHours(23, 59, 59, 999);
    }
  }

  return { start, end };
};

const parseNumber = (v, fallback = 0) => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isNaN(n) ? fallback : n;
  }
  return fallback;
};

const normalizeStatus = (status) => {
  if (!status) return "";
  return status.toString().toLowerCase();
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(parseNumber(value));

// ---------- component ----------

const ForecastReports = () => {
  const { restaurantId, loading: restaurantLoading } =
    useRestaurantContext() || {};

  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [ordersDocs, setOrdersDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        if (!restaurantId) {
          console.warn("[ForecastReports] No restaurantId yet, skipping fetch");
          setOrdersDocs([]);
          setLoading(false);
          return;
        }

        const ordersRef = collection(db, "orders");
        const ordersQuery = query(
          ordersRef,
          where("restaurantId", "==", restaurantId)
        );

        const snap = await getDocs(ordersQuery);

        const rows = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        console.log("[ForecastReports] loaded orders:", rows.length);
        if (rows[0]) {
          console.log("[ForecastReports] sample order:", rows[0]);
        }

        setOrdersDocs(rows);
      } catch (err) {
        console.error("[ForecastReports] Load orders failed:", err);
        setOrdersDocs([]);
      } finally {
        setLoading(false);
      }
    };

    if (restaurantLoading) {
      setLoading(true);
      return;
    }

    fetchOrders();
  }, [restaurantId, restaurantLoading]);

  const filteredOrders = useMemo(() => {
    if (!ordersDocs.length) return [];

    const { start, end } = resolveDateRange(dateRange);

    return ordersDocs
      .map((order) => {
        const date =
          getOrderDate(order.createdAt || order.placedAt || order.orderTime) ||
          null;

        const gross = parseNumber(
          order.subtotal ?? order.total ?? order.totalAmount
        );

        return {
          ...order,
          __date: date,
          __period: date ? date.toISOString().slice(0, 10) : "unknown",
          __gross: gross,
        };
      })
      .filter((order) => {
        const d = order.__date;
        if (!start && !end) return true;
        if (!d) return false;
        if (start && d < start) return false;
        if (end && d > end) return false;
        return true;
      });
  }, [ordersDocs, dateRange]);

  const dailyHistory = useMemo(() => {
    const map = new Map();

    filteredOrders.forEach((order) => {
      const key = order.__period;
      if (!key || key === "unknown") return;

      const current = map.get(key) || {
        id: key,
        period: key,
        orders: 0,
        grossSales: 0,
      };

      map.set(key, {
        ...current,
        orders: current.orders + 1,
        grossSales: current.grossSales + order.__gross,
      });
    });

    return [...map.values()].sort((a, b) =>
      a.period.localeCompare(b.period)
    );
  }, [filteredOrders]);

  const forecast = useMemo(() => {
    if (!dailyHistory.length) {
      return {
        avgDailyOrders: 0,
        avgDailySales: 0,
        next7Orders: 0,
        next7Sales: 0,
        next30Orders: 0,
        next30Sales: 0,
        futureDays: [],
      };
    }

    const totalDays = dailyHistory.length;
    const totalOrders = dailyHistory.reduce((sum, d) => sum + d.orders, 0);
    const totalSales = dailyHistory.reduce(
      (sum, d) => sum + d.grossSales,
      0
    );

    const avgDailyOrders = totalOrders / totalDays;
    const avgDailySales = totalSales / totalDays;

    const next7Orders = avgDailyOrders * 7;
    const next7Sales = avgDailySales * 7;
    const next30Orders = avgDailyOrders * 30;
    const next30Sales = avgDailySales * 30;

    const lastDateStr = dailyHistory[dailyHistory.length - 1].period;
    const lastDate = new Date(lastDateStr);
    const futureDays = [];

    for (let i = 1; i <= 14; i += 1) {
      const d = new Date(lastDate);
      d.setDate(d.getDate() + i);
      const period = d.toISOString().slice(0, 10);

      futureDays.push({
        id: `future-${period}`,
        period,
        orders: avgDailyOrders,
        grossSales: avgDailySales,
        isFuture: true,
      });
    }

    return {
      avgDailyOrders,
      avgDailySales,
      next7Orders,
      next7Sales,
      next30Orders,
      next30Sales,
      futureDays,
    };
  }, [dailyHistory]);

  const chartData = useMemo(() => {
    const historyTail =
      dailyHistory.length > 30
        ? dailyHistory.slice(dailyHistory.length - 30)
        : dailyHistory;

    const past = historyTail.map((d) => ({
      ...d,
      isFuture: false,
    }));

    return [...past, ...forecast.futureDays];
  }, [dailyHistory, forecast]);

  const kpis = useMemo(() => {
    return {
      historyDays: dailyHistory.length,
      avgDailyOrders: forecast.avgDailyOrders,
      avgDailySales: forecast.avgDailySales,
      next7Orders: forecast.next7Orders,
      next7Sales: forecast.next7Sales,
      next30Orders: forecast.next30Orders,
      next30Sales: forecast.next30Sales,
    };
  }, [dailyHistory.length, forecast]);

  if (restaurantLoading) {
    return (
      <div className="reports-section">
        <p className="muted">Loading restaurant…</p>
      </div>
    );
  }

  if (!restaurantId) {
    return (
      <div className="reports-section">
        <p className="muted">No restaurant found. Please sign in again.</p>
      </div>
    );
  }

  return (
    <div className="reports-section">
      <ReportFilterBar
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
      />

      <section className="reports-kpi-grid">
        <ReportCard
          title="Days of history"
          value={kpis.historyDays}
          subtitle="Used for this forecast"
        />
        <ReportCard
          title="Avg daily orders"
          value={kpis.avgDailyOrders.toFixed(1)}
        />
        <ReportCard
          title="Avg daily sales"
          value={formatCurrency(kpis.avgDailySales)}
        />
        <ReportCard
          title="Next 7 days – orders"
          value={kpis.next7Orders.toFixed(0)}
          subtitle={`${formatCurrency(kpis.next7Sales)} projected sales`}
        />
        <ReportCard
          title="Next 30 days – orders"
          value={kpis.next30Orders.toFixed(0)}
          subtitle={`${formatCurrency(kpis.next30Sales)} projected sales`}
        />
      </section>

      <section className="reports-row">
        <div className="reports-column">
          <h3 className="reports-section-title">
            Historical vs forecasted orders
          </h3>
          <ReportChart
            type="line"
            data={chartData}
            xKey="period"
            series={[
              {
                dataKey: "orders",
                name: "Orders",
              },
            ]}
          />
        </div>
      </section>

      <section className="reports-row">
        <div className="reports-column">
          <h3 className="reports-section-title">Recent daily history</h3>
          <ReportTable
            columns={[
              { title: "Day", dataIndex: "period", key: "period" },
              { title: "Orders", dataIndex: "orders", key: "orders" },
              {
                title: "Sales",
                dataIndex: "grossSales",
                key: "grossSales",
                render: (v) => formatCurrency(v),
              },
            ]}
            data={dailyHistory}
          />
        </div>

        <div className="reports-column">
          <h3 className="reports-section-title">
            Upcoming forecast (next 14 days)
          </h3>
          <ReportTable
            columns={[
              { title: "Day", dataIndex: "period", key: "period" },
              { title: "Forecast orders", dataIndex: "orders", key: "orders" },
              {
                title: "Forecast sales",
                dataIndex: "grossSales",
                key: "grossSales",
                render: (v) => formatCurrency(v),
              },
            ]}
            data={forecast.futureDays}
          />
        </div>
      </section>

      {loading && (
        <div className="reports-loading">Loading data…</div>
      )}

      {!loading && !dailyHistory.length && (
        <p className="reports-empty">
          Not enough order history to generate a forecast for this period.
        </p>
      )}
    </div>
  );
};

export default ForecastReports;
